/* Provider Chat (planner side) — mirror of the doctor's Lab Chat.
 *
 * Same two-pane primitive (inbox + thread + composer), but the inbox is
 * sourced from /api/coe/lab-chat/inbox (scoped to cases assigned to this
 * planner). The thread endpoint is shared with the doctor side — the
 * backend already allows planner / QA / production participation.
 *
 * URL: /coe/planner/provider-chat?case=<caseId>
 *   - Deep-linkable; the dashboard's preview rail uses this.
 *   - Refresh restores the same thread.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
    ChatCircleDots, PaperPlaneTilt, MagnifyingGlass, ArrowRight, Spinner, ArrowsClockwise,
    Paperclip, X, FileText, Image as ImageIcon, Check, Checks,
} from "@phosphor-icons/react";


const POLL_MS = 30_000;
const FAST_POLL_MS = 5_000;


function fmtTime(iso) {
    if (!iso) return "";
    const t = new Date(iso);
    if (Number.isNaN(t.getTime())) return "";
    const now = new Date();
    const sameDay = t.toDateString() === now.toDateString();
    if (sameDay) {
        return t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
    }
    const within7d = now.getTime() - t.getTime() < 7 * 86400000;
    if (within7d) {
        return t.toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit", hour12: false });
    }
    return t.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
}


function fmtSize(bytes) {
    if (!bytes || Number.isNaN(bytes)) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}


export default function PlannerProviderChat() {
    const [searchParams, setSearchParams] = useSearchParams();
    const activeCaseId = searchParams.get("case") || null;

    const [inbox, setInbox] = useState([]);
    const [inboxLoading, setInboxLoading] = useState(true);
    const [inboxQ, setInboxQ] = useState("");
    const [thread, setThread] = useState([]);
    const [threadLoading, setThreadLoading] = useState(false);
    const [draft, setDraft] = useState("");
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const [uploadingCount, setUploadingCount] = useState(0);
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);
    const pollTimerRef = useRef(null);
    const fileInputRef = useRef(null);

    const reloadInbox = useCallback(async () => {
        try {
            const { data } = await api.get("/coe/lab-chat/inbox");
            setInbox(data || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not load Provider Chat inbox");
        } finally {
            setInboxLoading(false);
        }
    }, []);

    useEffect(() => { reloadInbox(); }, [reloadInbox]);

    const reloadThread = useCallback(async (caseId, opts = {}) => {
        if (!caseId) { setThread([]); return; }
        if (!opts.silent) setThreadLoading(true);
        try {
            const { data } = await api.get(`/cases/${caseId}/messages`);
            setThread(data || []);
            if (!opts.skipMarkRead) {
                api.post(`/cases/${caseId}/messages/mark-read`)
                    .then((r) => { if (r.data?.marked > 0) reloadInbox(); })
                    .catch(() => { /* non-critical */ });
            }
        } catch (err) {
            if (!opts.silent) toast.error(err.response?.data?.detail || "Could not load thread");
        } finally {
            if (!opts.silent) setThreadLoading(false);
        }
    }, [reloadInbox]);

    useEffect(() => {
        setPendingAttachments([]);
        setDraft("");
        reloadThread(activeCaseId);
    }, [activeCaseId, reloadThread]);

    useEffect(() => {
        if (!activeCaseId) return undefined;
        let cancelled = false;
        const tick = async () => {
            if (cancelled || document.hidden) return;
            await reloadThread(activeCaseId, { silent: true, skipMarkRead: true });
        };
        pollTimerRef.current = setInterval(tick, POLL_MS);
        return () => {
            cancelled = true;
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        };
    }, [activeCaseId, reloadThread]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [thread.length]);

    const onFilesPicked = useCallback(async (files) => {
        if (!activeCaseId) return;
        const list = Array.from(files || []);
        if (list.length === 0) return;
        for (const file of list) {
            setUploadingCount((n) => n + 1);
            try {
                const form = new FormData();
                form.append("file", file);
                const { data } = await api.post(
                    `/cases/${activeCaseId}/upload?kind=chat_attachment`,
                    form,
                    { headers: { "Content-Type": "multipart/form-data" } },
                );
                setPendingAttachments((arr) => [...arr, {
                    id: data.id,
                    name: data.name,
                    content_type: data.content_type,
                    size: data.size,
                    url: `/api/files/${data.id}`,
                    is_image: (data.content_type || "").startsWith("image/"),
                }]);
            } catch (err) {
                toast.error(err.response?.data?.detail || `Couldn't upload ${file.name}`);
            } finally {
                setUploadingCount((n) => n - 1);
            }
        }
        if (fileInputRef.current) fileInputRef.current.value = "";
    }, [activeCaseId]);

    const removePendingAttachment = (id) => {
        setPendingAttachments((arr) => arr.filter((a) => a.id !== id));
    };

    const sendMessage = async () => {
        const text = draft.trim();
        const attachmentIds = pendingAttachments.map((a) => a.id);
        if ((!text && attachmentIds.length === 0) || sending || !activeCaseId) return;
        setSending(true);
        const optimisticId = `opt-${Date.now()}`;
        const optimistic = {
            id: optimisticId,
            author_name: "You",
            author_role: "staff",
            author_staff_type: "planner",
            text,
            attachments: pendingAttachments,
            read_by: [],
            created_at: new Date().toISOString(),
            _pending: true,
        };
        setThread((m) => [...m, optimistic]);
        setDraft("");
        setPendingAttachments([]);
        try {
            const { data } = await api.post(`/cases/${activeCaseId}/messages`, {
                text,
                attachment_file_ids: attachmentIds,
            });
            setThread((m) => m.map((x) => (x.id === optimisticId ? data : x)));
            reloadInbox();
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
            let bursts = 0;
            const fast = setInterval(() => {
                bursts += 1;
                if (bursts > 6) {
                    clearInterval(fast);
                    pollTimerRef.current = setInterval(
                        () => !document.hidden && reloadThread(activeCaseId, { silent: true, skipMarkRead: true }),
                        POLL_MS,
                    );
                    return;
                }
                if (!document.hidden) reloadThread(activeCaseId, { silent: true, skipMarkRead: true });
            }, FAST_POLL_MS);
            pollTimerRef.current = fast;
        } catch (err) {
            setThread((m) => m.filter((x) => x.id !== optimisticId));
            setDraft(text);
            setPendingAttachments(optimistic.attachments || []);
            toast.error(err.response?.data?.detail || "Couldn't send message");
        } finally {
            setSending(false);
        }
    };

    const filteredInbox = useMemo(() => {
        const q = inboxQ.trim().toLowerCase();
        if (!q) return inbox;
        return inbox.filter((c) =>
            (c.patient_name || "").toLowerCase().includes(q) ||
            (c.doctor_name || "").toLowerCase().includes(q) ||
            (c.code || "").toLowerCase().includes(q) ||
            (c.last_message_preview || "").toLowerCase().includes(q)
        );
    }, [inbox, inboxQ]);

    const setActive = (caseId) => {
        const next = new URLSearchParams(searchParams);
        if (caseId) next.set("case", caseId); else next.delete("case");
        setSearchParams(next, { replace: true });
    };

    const activeCaseMeta = inbox.find((c) => c.id === activeCaseId);

    return (
        <div data-testid="planner-provider-chat" className="h-[calc(100vh-9rem)] min-h-[500px] flex gap-4">
            <aside className="w-[320px] shrink-0 bg-card border border-border rounded-2xl flex flex-col overflow-hidden">
                <div className="p-3 border-b border-border space-y-2.5">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5">
                            <ChatCircleDots size={15} weight="duotone" className="text-primary" />
                            <h2 className="font-display text-sm font-bold">Provider Chat</h2>
                        </div>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-[11px]"
                            onClick={() => { reloadInbox(); if (activeCaseId) reloadThread(activeCaseId); }}
                            data-testid="planner-chat-refresh"
                        >
                            <ArrowsClockwise size={11} className="mr-1" /> Refresh
                        </Button>
                    </div>
                    <div className="relative">
                        <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            value={inboxQ}
                            onChange={(e) => setInboxQ(e.target.value)}
                            placeholder="Search patient, doctor or case…"
                            className="w-full h-8 pl-7 pr-2 text-xs rounded-md border border-border bg-background"
                            data-testid="planner-chat-search"
                        />
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto" data-testid="planner-chat-inbox">
                    {inboxLoading ? (
                        <InboxSkeleton />
                    ) : filteredInbox.length === 0 ? (
                        <InboxEmpty hasFilter={inboxQ.length > 0} />
                    ) : (
                        filteredInbox.map((c) => (
                            <InboxRow
                                key={c.id}
                                row={c}
                                active={c.id === activeCaseId}
                                onClick={() => setActive(c.id)}
                            />
                        ))
                    )}
                </div>
            </aside>

            <div className="flex-1 min-w-0 bg-card border border-border rounded-2xl flex flex-col overflow-hidden">
                {!activeCaseId ? (
                    <ThreadEmpty hasInbox={inbox.length > 0} />
                ) : (
                    <>
                        <ThreadHeader meta={activeCaseMeta} caseId={activeCaseId} />
                        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="planner-chat-thread">
                            {threadLoading && thread.length === 0 ? (
                                <div className="text-center text-muted-foreground text-xs py-12">
                                    <Spinner size={20} className="mx-auto mb-2 animate-spin opacity-60" />
                                    Loading thread…
                                </div>
                            ) : thread.length === 0 ? (
                                <ThreadBlank />
                            ) : (
                                thread.map((m) => <MessageBubble key={m.id} msg={m} />)
                            )}
                        </div>
                        <Composer
                            draft={draft} setDraft={setDraft}
                            onSend={sendMessage}
                            sending={sending}
                            pendingAttachments={pendingAttachments}
                            onFilesPicked={onFilesPicked}
                            removePendingAttachment={removePendingAttachment}
                            uploadingCount={uploadingCount}
                            fileInputRef={fileInputRef}
                        />
                    </>
                )}
            </div>
        </div>
    );
}


/* ─────────── Inbox subcomponents ─────────── */
function InboxSkeleton() {
    return (
        <div className="p-3 space-y-3">
            {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-1.5 animate-pulse">
                    <div className="h-3 bg-muted rounded w-2/3" />
                    <div className="h-2 bg-muted rounded w-1/2" />
                </div>
            ))}
        </div>
    );
}


function InboxEmpty({ hasFilter }) {
    return (
        <div className="p-6 text-center">
            <ChatCircleDots size={26} weight="duotone" className="mx-auto mb-2 text-muted-foreground/60" />
            <p className="text-xs text-muted-foreground">
                {hasFilter ? "No matches in this view." : "No clinic conversations yet."}
            </p>
            {!hasFilter && (
                <p className="text-[10px] text-muted-foreground/80 mt-2 leading-snug">
                    When a partner clinic messages you on a case, the thread shows up here.
                </p>
            )}
        </div>
    );
}


function InboxRow({ row, active, onClick }) {
    const unread = row.unread_count || 0;
    return (
        <button
            type="button"
            onClick={onClick}
            className={`w-full text-left px-3 py-2.5 border-b border-border last:border-b-0 transition-colors ${
                active ? "bg-primary/10" : "hover:bg-secondary/50"
            }`}
            data-testid={`planner-chat-inbox-row-${row.id}`}
        >
            <div className="flex items-center justify-between gap-2">
                <div className={`text-sm truncate ${unread > 0 ? "font-bold" : "font-semibold"}`}>
                    {row.patient_name || "Patient"}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    {unread > 0 && (
                        <span
                            className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[10px] font-bold tabular-nums"
                            data-testid={`planner-chat-unread-${row.id}`}
                        >
                            {unread > 99 ? "99+" : unread}
                        </span>
                    )}
                    <div className="text-[10px] text-muted-foreground">{fmtTime(row.last_message_at)}</div>
                </div>
            </div>
            <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-muted-foreground">
                {row.patient_code && <span className="font-mono text-primary">{row.patient_code}</span>}
                {row.patient_code && <span>·</span>}
                <span className="truncate">Dr. {(row.doctor_name || "").replace(/^Dr\.\s*/i, "")}</span>
            </div>
            <div className={`text-[11px] mt-1 line-clamp-2 leading-snug ${
                unread > 0 ? "text-foreground font-medium" : "text-muted-foreground"
            }`}>
                {row.last_message_preview || "—"}
            </div>
        </button>
    );
}


/* ─────────── Thread subcomponents ─────────── */
function ThreadHeader({ meta, caseId }) {
    return (
        <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-2">
            <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{meta?.patient_name || "Patient"}</div>
                <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 mt-0.5 flex-wrap">
                    {meta?.patient_code && <span className="font-mono text-primary">{meta.patient_code}</span>}
                    {meta?.patient_code && <span>·</span>}
                    <span className="font-mono">{meta?.code || "—"}</span>
                    {meta?.doctor_name && (
                        <>
                            <span>·</span>
                            <span className="truncate">Dr. {meta.doctor_name.replace(/^Dr\.\s*/i, "")}</span>
                        </>
                    )}
                    {meta?.clinic_name && (
                        <>
                            <span>·</span>
                            <span className="truncate">{meta.clinic_name}</span>
                        </>
                    )}
                </div>
            </div>
            <Link
                to={`/coe/planner/cases/${caseId}`}
                className="text-[11px] font-semibold text-primary hover:underline whitespace-nowrap shrink-0"
                data-testid="planner-chat-open-case"
            >
                Open case <ArrowRight size={10} className="inline" />
            </Link>
        </div>
    );
}


function ThreadEmpty({ hasInbox }) {
    return (
        <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center max-w-sm">
                <ChatCircleDots size={32} weight="duotone" className="mx-auto mb-3 text-muted-foreground/60" />
                <h3 className="font-display text-base font-bold">
                    {hasInbox ? "Pick a case from the left" : "No conversations yet"}
                </h3>
                <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                    {hasInbox
                        ? "Provider Chat is your per-case thread with the partner clinic. Doctors see your messages as \"Center of Excellence — Planning\"."
                        : "When a clinic messages you on a case you're assigned to, the thread will show up here."}
                </p>
            </div>
        </div>
    );
}


function ThreadBlank() {
    return (
        <div className="text-center text-muted-foreground text-xs py-12">
            <ChatCircleDots size={22} weight="duotone" className="mx-auto mb-2 text-muted-foreground/60" />
            No messages yet — say hello to the clinic.
        </div>
    );
}


function MessageBubble({ msg }) {
    // "Mine" = the current COE staff message. Doctor / clinic_staff / admin
    // render on the left with their attribution chip.
    const mine = msg.author_role === "staff";
    const meta = (() => {
        if (msg.author_role === "admin") return { label: "Admin", tone: "bg-violet-50 text-violet-700 border-violet-200" };
        if (msg.author_role === "clinic_staff") return { label: `${msg.author_name} · Clinic`, tone: "bg-amber-50 text-amber-800 border-amber-200" };
        if (msg.author_role === "doctor") return { label: msg.author_name, tone: "bg-primary/10 text-primary border-primary/30" };
        return { label: msg.author_name, tone: "bg-secondary text-foreground/80 border-border" };
    })();
    const attachments = msg.attachments || [];
    const recipientReads = (msg.read_by || []).filter((r) => r.user_id !== msg.author_id).length;
    const showCheck = mine && !msg._pending;
    return (
        <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`planner-chat-msg-${msg.id}`}>
            <div className="max-w-[78%] min-w-0">
                {!mine && (
                    <div className={`inline-block text-[10px] px-1.5 py-0.5 rounded font-semibold border mb-1 ${meta.tone}`}>
                        {meta.label}
                    </div>
                )}
                <div className={`rounded-2xl px-3.5 py-2 text-sm break-words shadow-sm ${
                    mine
                        ? "bg-primary text-white rounded-br-md"
                        : "bg-secondary/70 text-foreground rounded-bl-md"
                } ${msg._pending ? "opacity-60" : ""}`}>
                    {attachments.length > 0 && (
                        <div className={`space-y-1.5 ${msg.text ? "mb-2" : ""}`}>
                            {attachments.map((a) => <AttachmentChip key={a.id} att={a} mine={mine} />)}
                        </div>
                    )}
                    {msg.text && <div className="whitespace-pre-wrap">{msg.text}</div>}
                </div>
                <div className={`text-[10px] mt-1 flex items-center gap-1 ${mine ? "justify-end text-muted-foreground" : "text-muted-foreground"}`}>
                    <span>{fmtTime(msg.created_at)}{msg._pending && " · sending…"}</span>
                    {showCheck && (
                        recipientReads > 0 ? (
                            <Checks size={12} weight="bold" className="text-primary" title="Read by the clinic" data-testid={`planner-chat-msg-read-${msg.id}`} />
                        ) : (
                            <Check size={12} weight="bold" className="text-muted-foreground/70" title="Sent — not yet read" data-testid={`planner-chat-msg-sent-${msg.id}`} />
                        )
                    )}
                </div>
            </div>
        </div>
    );
}


function AttachmentChip({ att, mine }) {
    const isImage = (att.content_type || "").startsWith("image/")
        || /\.(png|jpg|jpeg|gif|webp|heic)$/i.test(att.name || "");
    const [blobUrl, setBlobUrl] = useState(null);
    useEffect(() => {
        if (!isImage || !att.id) return undefined;
        let revoked = false;
        let url = null;
        api.get(`/files/${att.id}`, { responseType: "blob" })
            .then((r) => {
                if (revoked) return;
                url = URL.createObjectURL(r.data);
                setBlobUrl(url);
            })
            .catch(() => {});
        return () => {
            revoked = true;
            if (url) URL.revokeObjectURL(url);
        };
    }, [att.id, isImage]);

    const downloadFile = async () => {
        try {
            const r = await api.get(`/files/${att.id}`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = att.name || "download";
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1500);
        } catch {
            toast.error("Couldn't download attachment");
        }
    };

    if (isImage) {
        return (
            <button
                type="button"
                onClick={downloadFile}
                className="block max-w-[260px] rounded-lg overflow-hidden border border-black/10 bg-black/5 cursor-zoom-in"
                data-testid={`planner-chat-attachment-${att.id}`}
                title={att.name}
            >
                {blobUrl ? (
                    <img src={blobUrl} alt={att.name} className="block max-h-[200px] w-auto" />
                ) : (
                    <div className="w-[200px] h-[120px] flex items-center justify-center text-xs text-muted-foreground">
                        <Spinner size={16} className="animate-spin" />
                    </div>
                )}
            </button>
        );
    }
    const cardCls = mine
        ? "bg-white/10 hover:bg-white/20 border-white/20 text-white"
        : "bg-card hover:bg-secondary border-border text-foreground";
    return (
        <button
            type="button"
            onClick={downloadFile}
            className={`flex items-center gap-2 max-w-[260px] px-2.5 py-2 rounded-lg border text-left ${cardCls}`}
            data-testid={`planner-chat-attachment-${att.id}`}
        >
            <FileText size={18} weight="duotone" className="shrink-0" />
            <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold truncate">{att.name || "File"}</div>
                <div className={`text-[10px] ${mine ? "text-white/70" : "text-muted-foreground"}`}>
                    {fmtSize(att.size)}
                </div>
            </div>
        </button>
    );
}


function Composer({
    draft, setDraft, onSend, sending,
    pendingAttachments = [], onFilesPicked,
    removePendingAttachment, uploadingCount = 0, fileInputRef,
}) {
    const onKey = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            onSend();
        }
    };
    const canSend = !sending && uploadingCount === 0 && (draft.trim().length > 0 || pendingAttachments.length > 0);
    return (
        <div className="border-t border-border p-3 bg-background/50">
            {(pendingAttachments.length > 0 || uploadingCount > 0) && (
                <div className="mb-2 flex flex-wrap gap-2" data-testid="planner-chat-attachments-tray">
                    {pendingAttachments.map((a) => (
                        <PendingAttachmentChip
                            key={a.id}
                            att={a}
                            onRemove={() => removePendingAttachment(a.id)}
                        />
                    ))}
                    {uploadingCount > 0 && (
                        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-secondary/70 border border-border text-[11px] text-muted-foreground">
                            <Spinner size={12} className="animate-spin" />
                            Uploading {uploadingCount} file{uploadingCount === 1 ? "" : "s"}…
                        </div>
                    )}
                </div>
            )}
            <div className="flex items-end gap-2">
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => onFilesPicked(e.target.files)}
                    className="hidden"
                    multiple
                    accept="image/*,application/pdf,.stl,.ply,.obj"
                    data-testid="planner-chat-file-input"
                />
                <Button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                    size="sm"
                    className="h-10 px-2.5 shrink-0"
                    title="Attach files"
                    data-testid="planner-chat-attach-btn"
                >
                    <Paperclip size={15} />
                </Button>
                <Textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onKey}
                    placeholder="Type your message to the clinic…"
                    rows={2}
                    className="resize-none text-sm"
                    data-testid="planner-chat-composer"
                />
                <Button
                    type="button"
                    onClick={onSend}
                    disabled={!canSend}
                    size="sm"
                    className="h-10 shrink-0"
                    data-testid="planner-chat-send"
                >
                    {sending ? <Spinner size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="fill" />}
                    <span className="ml-1.5 hidden sm:inline">Send</span>
                </Button>
            </div>
            <div className="text-[10px] text-muted-foreground mt-1.5">
                Press Cmd / Ctrl + Enter to send. Doctors see your name as "Center of Excellence — Planning".
            </div>
        </div>
    );
}


function PendingAttachmentChip({ att, onRemove }) {
    return (
        <div className="relative inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-md bg-secondary border border-border text-[11px]" data-testid={`planner-chat-tray-${att.id}`}>
            {att.is_image
                ? <ImageIcon size={13} weight="duotone" className="text-primary shrink-0" />
                : <FileText size={13} weight="duotone" className="text-muted-foreground shrink-0" />}
            <span className="font-medium truncate max-w-[140px]">{att.name}</span>
            <button
                type="button"
                onClick={onRemove}
                className="w-5 h-5 rounded-full hover:bg-rose-100 hover:text-rose-700 flex items-center justify-center transition-colors"
                title="Remove"
                data-testid={`planner-chat-tray-remove-${att.id}`}
            >
                <X size={11} weight="bold" />
            </button>
        </div>
    );
}
